import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Sentry from '@sentry/react';
import { partition } from 'lodash-es';

import {
  getQuestionOption,
  QUESTION_QUOTA_LOGICAL_MODIFIER_OPTIONS,
} from '../../../../util/formOptions';
import { validateQuotas } from '../../../../util/questions';
import { QuestionFormData, ReactSelectValue } from '../../../../types/forms';
import { QuotaEligibleTarget } from '../../../../util/demographics';
import { showErrorMessage } from '../../../../util/notifications';
import CircleLoader from '../../../common/CircleLoader';
import Modal, { ModalHeader } from '../../../common/Modal';

import { QuotasForm } from './QuotasForm';
import { useQueries } from '@tanstack/react-query';
import { surveyAudienceTargetQueries } from 'hooks/backend/surveyAudienceTarget';
import {
  useCreateQuestionQuotasBulk,
  useUpdateQuestionQuota,
} from 'hooks/backend/questionQuotas';
import { QuestionQuotaBody, Question } from 'types/domainModels';

export type QuotaFeature = QuestionFormData['features']['quotas'];
export type QuotaValues = QuotaFeature['values'];

export type QuotaRowFormData = QuotaValues[number] & {
  option: ReactSelectValue<Question> | null;
  surveyAudienceTargetId: number;
};

export interface QuotasFormData {
  modalQuotas: QuotaRowFormData[];
}

function getInitialQuotaLogicalModifier(targetLogicalModifierLabel: string) {
  const value = targetLogicalModifierLabel === 'are not' ? 'none' : 'all';

  return QUESTION_QUOTA_LOGICAL_MODIFIER_OPTIONS.find(
    (option) => option.value === value,
  )!;
}

export const QuotasModal = ({
  demographicQuestions,
  onCloseModal,
  targets,
  waveId,
}: {
  demographicQuestions: Question[];
  onCloseModal(): void;
  targets: QuotaEligibleTarget[];
  waveId: number;
}) => {
  const [validateOnBlur, setValidateOnBlur] = useState(false);

  const createQuotasBulk = useCreateQuestionQuotasBulk({
    onError: (error) => {
      Sentry.captureException(error);
      showErrorMessage({ err: error });
    },
  });

  const updateQuestionQuota = useUpdateQuestionQuota({
    onError: (error) => {
      Sentry.captureException(error);
      showErrorMessage({ err: error });
    },
  });

  const targetQuestionQueries = useQueries({
    queries: targets.map(({ target }) =>
      surveyAudienceTargetQueries.targetQuestion({
        surveyAudienceTargetId: target.id,
      }),
    ),
  });

  const isLoadingTargetQuestions = targetQuestionQueries.some(
    (query) => query.isLoading,
  );
  const isErrorTargetQuestions = targetQuestionQueries.some(
    (query) => query.isError,
  );

  useEffect(() => {
    if (isErrorTargetQuestions) {
      Sentry.captureException(
        new Error('Failed to fetch target question(s) for the quotas modal'),
      );
      showErrorMessage({
        message:
          "Something went wrong loading this survey's targets. Some quotas below may not be pre-filled correctly.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorTargetQuestions]);

  const initialModalQuotas: QuotaRowFormData[] = targets.map(
    ({ target }, index) => {
      const targetQuestion = targetQuestionQueries[index]?.data;
      const foundQuestion =
        demographicQuestions.find(
          (question) => question.id === targetQuestion?.questionId,
        ) ?? null;
      const foundQuestionOptionIds = targetQuestion?.questionOptionIds ?? [];

      return {
        isQualityDisqualification: false,
        logicalModifier: getInitialQuotaLogicalModifier(target.logicalModifier),
        numberNeeded: '',
        option: foundQuestion
          ? getQuestionOption({ question: foundQuestion })
          : null,
        options: (foundQuestion?.options ?? [])
          .filter((option) => foundQuestionOptionIds.includes(option.id))
          .map((option) => ({
            label: option.title,
            value: { id: option.id, title: option.title },
          })),
        quotaId: null,
        surveyAudienceTargetId: target.id,
      };
    },
  );

  return (
    <Modal
      header={
        <ModalHeader onClickClose={onCloseModal}>
          Create Quotas from Targets
        </ModalHeader>
      }
      onCloseModal={onCloseModal}
      position="top"
      size="auto"
    >
      {isLoadingTargetQuestions ? (
        <div className="flex justify-center p-12">
          <CircleLoader isColored={true} />
        </div>
      ) : (
        <>
          {targets.length > 0 && (
            <p className="mb-4 text-sm text-gray-d-700">
              These targets are set to 0% or 100%:{' '}
              <ul className="list-disc pl-8">
                {targets.map(({ target }, index) => (
                  <li
                    key={index}
                  >{`${target.percentage}% ${target.logicalModifier} ${target.audienceGroupOptionSelections}`}</li>
                ))}
              </ul>
              Consider adding the matching quota(s) below.
            </p>
          )}

          <Formik<QuotasFormData>
            initialValues={{
              modalQuotas: initialModalQuotas,
            }}
            onSubmit={async (formData) => {
              const validQuotas = formData.modalQuotas.filter(
                (quota) => quota.option !== null,
              );

              const [quotasToUpdate, quotasToCreate] = partition(
                validQuotas,
                (quota) =>
                  targets.find(
                    (t) => t.target.id === quota.surveyAudienceTargetId,
                  )?.target.questionQuotaId !== null,
              );

              const updatePromises = quotasToUpdate.map((quota) => {
                const existingQuotaId = targets.find(
                  (t) => t.target.id === quota.surveyAudienceTargetId,
                )!.target.questionQuotaId!;

                return updateQuestionQuota.mutateAsync({
                  questionQuotaId: existingQuotaId,
                  data: {
                    surveyAudienceTargetId: quota.surveyAudienceTargetId,
                    logicalModifier: quota.logicalModifier!.value,
                    options: quota.options.map((option) => option.value),
                    questionId: quota.option!.value.id,
                    waveId,
                  },
                });
              });

              const data: QuestionQuotaBody[] = quotasToCreate.map((quota) => ({
                disqualificationType: quota.isQualityDisqualification
                  ? 'QUALITY'
                  : 'NON_QUALITY',
                logicalModifier: quota.logicalModifier!.value,
                numberNeeded:
                  quota.numberNeeded === '' ? undefined : quota.numberNeeded,
                options: quota.options.map((option) => option.value),
                questionId: quota.option!.value.id,
                surveyAudienceTargetId: quota.surveyAudienceTargetId,
                waveId,
              }));

              const createPromise =
                data.length > 0 ? createQuotasBulk.mutateAsync({ data }) : null;

              try {
                await Promise.all([...updatePromises, createPromise]);
                onCloseModal();
              } catch {
                // errors are already reported via each mutation's onError
              }
            }}
            validate={(formData) => {
              const errors = validateQuotas(formData.modalQuotas);
              if (errors) {
                return { modalQuotas: errors };
              }

              return {};
            }}
            validateOnBlur={validateOnBlur}
            validateOnChange={false}
          >
            <QuotasForm
              demographicQuestions={demographicQuestions}
              isSaving={
                createQuotasBulk.isPending || updateQuestionQuota.isPending
              }
              onClickClose={onCloseModal}
              onSubmit={() => {
                setValidateOnBlur(true);
              }}
            />
          </Formik>
        </>
      )}
    </Modal>
  );
};
